import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { Urls, AGENT_PATH_RE } from '@/lib/navigation';
import { SessionProvider } from '@/contexts/SessionContext';
import { ChatLayout } from '@/components/ChatLayout';
import { LandingPage } from '@/pages/LandingPage';
import { ConversationPage } from '@/pages/ConversationPage';
import { DemosPage } from '@/pages/DemosPage';
import { DemoActivatePage } from '@/pages/DemoActivatePage';
import { AgentsPage } from '@/pages/AgentsPage';
import { GeneralSettingsView } from '@/components/settings/GeneralSettingsView';
import { BillingSettingsView } from '@/components/settings/BillingSettingsView';
import { useAuth } from '@/hooks/useAuth';
import { useSubscription } from '@/hooks/useSubscription';
import { useIsMobileDevice } from '@/hooks/useIsMobileDevice';
import { useArtifactState } from '@/hooks/useArtifactState';
import { useSessionLifecycle } from '@/hooks/useSessionLifecycle';
import { useAgentActions } from '@/hooks/useAgentActions';
import { useAnonSessionStore } from '@/stores/anonSession';
import type { AgentCanvasChatContext } from '@/artifact/types';

export default function App() {
  const isMobileDevice = useIsMobileDevice();
  const auth = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [agentError, setAgentError] = useState<string | null>(null);
  const collectAgentCanvasContextRef = useRef<() => Promise<AgentCanvasChatContext | null>>(
    async () => null,
  );
  const clearAgentCanvasContextRef = useRef<() => void>(() => {});

  const currentSessionId = location.pathname.match(AGENT_PATH_RE)?.[1] ?? null;

  const session = useSessionLifecycle({
    currentSessionId,
    onError: setAgentError,
    collectAgentCanvasContext: useCallback(
      () => collectAgentCanvasContextRef.current(),
      [],
    ),
    clearAgentCanvasContext: useCallback(
      () => clearAgentCanvasContextRef.current(),
      [],
    ),
  });

  // Force anon users back to their running agent
  const { sessionId: savedAnonSession, setSessionId: setSavedAnonSession } = useAnonSessionStore();
  const didInitialRedirect = useRef(false);

  // Immediate redirect from sessionStorage before SSE loads
  useEffect(() => {
    if (didInitialRedirect.current) return;
    didInitialRedirect.current = true;
    if (auth.isAuthenticated) return;
    if (!savedAnonSession) return;
    const target = Urls.agent(savedAnonSession);
    if (location.pathname !== target) {
      navigate(target, { replace: true });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Keep the store in sync with live session data, and redirect if needed
  const anonForceTarget = useMemo(() => {
    if (auth.isAuthenticated) return null;
    const running = session.chatHistory.sessions.find((s) => s.runtime_status !== 'off' && !s.is_example);
    return running?.id ?? null;
  }, [auth.isAuthenticated, session.chatHistory.sessions]);

  useEffect(() => {
    setSavedAnonSession(anonForceTarget);
  }, [anonForceTarget, setSavedAnonSession]);

  // Redirect anon users to their running agent only on initial SSE sync,
  // not on subsequent navigations (so they can browse demos and go home).
  const didAnonRedirect = useRef(false);
  useEffect(() => {
    if (didAnonRedirect.current) return;
    if (!anonForceTarget) return;
    didAnonRedirect.current = true;
    const target = Urls.agent(anonForceTarget);
    if (location.pathname !== target) {
      navigate(target, { replace: true });
    }
  }, [anonForceTarget, location.pathname, navigate]);

  const currentAgentStatus = useMemo(
    () => session.chatHistory.sessions.find((s) => s.id === currentSessionId)?.runtime_status,
    [session.chatHistory.sessions, currentSessionId],
  );
  const artifacts = useArtifactState(currentSessionId, isMobileDevice, currentAgentStatus);
  collectAgentCanvasContextRef.current = artifacts.artifactEngine.collectAgentCanvasContext;
  clearAgentCanvasContextRef.current = artifacts.artifactEngine.clearAgentCanvasContext;

  const actions = useAgentActions({
    session,
    setAgentError,
  });

  const dismissAgentError = useCallback(() => setAgentError(null), []);

  // Clear a latched payment-required ChatError on the *transition* into
  // a billing state that allows sends (e.g. user just subscribed). The
  // server is authoritative — if we cleared optimistically and the user
  // still can't send, the next attempt re-sets the error. Transition-only
  // so a paid user who hits their plan cap mid-session stays latched.
  const { isActive, currentPlanId, loading: subLoading } = useSubscription();
  const ableToSend = auth.isAuthenticated && isActive && currentPlanId !== 'free';
  // null until the subscription fetch resolves, so a paid user refreshing the
  // page (where ableToSend goes false→true as the fetch lands) isn't mistaken
  // for an upgrade transition that should clear a latched error.
  const prevAbleToSendRef = useRef<boolean | null>(null);
  const activeChatId = session.chat.activeChat?.sessionId;
  const hasPaymentRequired = session.chat.activeChat?.error?.type === 'payment-required';
  const clearChatError = session.chat.clearError;
  useEffect(() => {
    if (subLoading) return;

    const wasAble = prevAbleToSendRef.current;

    if (!ableToSend) {
      prevAbleToSendRef.current = false;
      return;
    }

    if (!activeChatId) return;

    prevAbleToSendRef.current = true;
    if (wasAble === false && hasPaymentRequired) {
      clearChatError(activeChatId);
    }
  }, [subLoading, ableToSend, hasPaymentRequired, activeChatId, clearChatError]);

  return (
    <SessionProvider value={{ session, actions, artifacts, agentError, dismissAgentError }}>
      <div className="flex h-full bg-zinc-950 text-zinc-100">
        <Routes>
          <Route path={Urls.agents()} element={<AgentsPage />} />
          <Route path={Urls.settings()} element={<GeneralSettingsView />} />
          <Route path={Urls.billing()} element={<BillingSettingsView />} />
          <Route element={<ChatLayout />}>
            <Route index element={<LandingPage />} />
            <Route path="agent/:id" element={<ConversationPage />} />
            <Route path={Urls.demos().slice(1)} element={<DemosPage />} />
            <Route path="demos/:name" element={<DemoActivatePage />} />
          </Route>
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </div>
    </SessionProvider>
  );
}
